'use client';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box } from '@kohler-dsm-library/kohler-core';
import { ArrowRightIcon, ArrowLeftIcon } from '@kohler-dsm-library/kohler-icons';
import Thumbnails from './Thumbnails';
import { buildDsmImageProps } from '@/aem-dsm-mappers/utils/imageProps';
import styles from '../productFeatureCarousel.module.css';

export default function ImageCarousel({ images = [] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [previewIndex, setPreviewIndex] = useState(images.length > 1 ? 1 : 0);

  const containerRef = useRef(null);
  const trackRef = useRef(null);

  const isDragging = useRef(false);
  const startX = useRef(0);
  const currentTranslate = useRef(0);
  const prevTranslate = useRef(0);
  const animationRef = useRef(null);
  const activeIndexRef = useRef(0);
  const wheelTimeout = useRef(null);

  if (!images.length) return null;

  // ─── Helpers ───
  const getSlideWidth = () => {
    const el = containerRef.current;
    if (!el) return 0;
    return el.clientWidth;
  };

  const applyTransform = (px, animate = false) => {
    if (!trackRef.current) return;
    trackRef.current.style.transition = animate ? 'transform 0.3s ease-out' : 'none';
    trackRef.current.style.transform = `translateX(${px}px)`;
  };

  const snapToIndex = (index, animate = true) => {
    const pos = -(index * getSlideWidth());
    currentTranslate.current = pos;
    prevTranslate.current = pos;
    activeIndexRef.current = index;
    applyTransform(pos, animate);
  };

  const updatePreview = (idx) => {
    setPreviewIndex(Math.min(idx + 1, images.length - 1));
  };

  // ─── Trackpad / Mouse Wheel ───
  const onWheel = (e) => {
    // Only respond to horizontal scroll (trackpad gesture)
    if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;

    e.preventDefault();

    const slideWidth = getSlideWidth();
    if (!slideWidth) return;

    // Move the track by deltaX
    currentTranslate.current -= e.deltaX;

    // Clamp
    const minTranslate = -(images.length - 1) * slideWidth;
    currentTranslate.current = Math.max(minTranslate, Math.min(0, currentTranslate.current));

    applyTransform(currentTranslate.current, false);

    // Live preview update
    const progress = -currentTranslate.current / slideWidth;
    const closest = Math.round(progress);
    const clamped = Math.max(0, Math.min(closest, images.length - 1));
    updatePreview(clamped);

    // Snap to nearest slide when scrolling stops (150ms debounce)
    clearTimeout(wheelTimeout.current);
    wheelTimeout.current = setTimeout(() => {
      const finalIndex = Math.round(-currentTranslate.current / slideWidth);
      const snappedIndex = Math.max(0, Math.min(finalIndex, images.length - 1));
      setActiveIndex(snappedIndex);
      updatePreview(snappedIndex);
      snapToIndex(snappedIndex, true);
    }, 150);
  };

  // ─── Drag Start ───
  const onDragStart = (e) => {
    isDragging.current = true;
    startX.current = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;

    if (trackRef.current) trackRef.current.style.transition = 'none';

    const animate = () => {
      applyTransform(currentTranslate.current, false);
      if (isDragging.current) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };
    animationRef.current = requestAnimationFrame(animate);
  };

  // ─── Drag Move ───
  const onDragMove = (e) => {
    if (!isDragging.current) return;
    const currentX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;

    const diff = currentX - startX.current;
    currentTranslate.current = prevTranslate.current + diff;

    // Clamp: can't drag past first/last
    const slideWidth = getSlideWidth();
    const minTranslate = -(images.length - 1) * slideWidth;
    currentTranslate.current = Math.max(minTranslate, Math.min(0, currentTranslate.current));

    // Live preview update
    if (slideWidth) {
      const progress = -currentTranslate.current / slideWidth;
      const closest = Math.round(progress);
      const clamped = Math.max(0, Math.min(closest, images.length - 1));
      updatePreview(clamped);
    }
  };

  // ─── Drag End ───
  const onDragEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    cancelAnimationFrame(animationRef.current);

    const slideWidth = getSlideWidth();
    const movedBy = currentTranslate.current - prevTranslate.current;
    const currentIdx = activeIndexRef.current;
    let newIndex = currentIdx;

    // Dragged > 20% → go to next/prev; otherwise snap back
    if (movedBy < -(slideWidth * 0.2) && currentIdx < images.length - 1) {
      newIndex = currentIdx + 1;
    } else if (movedBy > slideWidth * 0.2 && currentIdx > 0) {
      newIndex = currentIdx - 1;
    }

    setActiveIndex(newIndex);
    updatePreview(newIndex);
    snapToIndex(newIndex, true);
  };

  // ─── Button Navigation ───
  const goNext = () => {
    if (activeIndex >= images.length - 1) return;
    const next = activeIndex + 1;
    setActiveIndex(next);
    updatePreview(next);
    snapToIndex(next, false);
  };

  const goPrev = () => {
    if (activeIndex <= 0) return;
    const prev = activeIndex - 1;
    setActiveIndex(prev);
    updatePreview(prev);
    snapToIndex(prev, false);
  };

  // ─── Thumbnail Navigation ───
  const handleThumbnailClick = useCallback(
    (index) => {
      setActiveIndex(index);
      setPreviewIndex(Math.min(index + 1, images.length - 1));
      snapToIndex(index, false);
    },
    [images.length],
  );

  // ─── Initial position + resize ───
  useEffect(() => {
    snapToIndex(activeIndex, false);

    const onResize = () => snapToIndex(activeIndexRef.current, false);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [images.length]);

  useEffect(() => {
    snapToIndex(activeIndex, false);

    const onResize = () => snapToIndex(activeIndexRef.current, false);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(wheelTimeout.current);
    };
  }, []);

  return (
    <>
      <Box className={styles.carouselRoot}>
        {/* ===== MAIN CAROUSEL VIEWPORT ===== */}
        <Box
          ref={containerRef}
          className={styles.carouselScroll}
          onMouseDown={onDragStart}
          onMouseMove={onDragMove}
          onMouseUp={onDragEnd}
          onMouseLeave={() => {
            if (isDragging.current) onDragEnd();
          }}
          onTouchStart={onDragStart}
          onTouchMove={onDragMove}
          onTouchEnd={onDragEnd}
        >
          <Box ref={trackRef} className={styles.carouselTrack}>
            {images.map((img, index) => (
              <Box className={styles.carouselItem} key={img.image || index}>
                <img
                  {...buildDsmImageProps({
                    src: img.image,
                    alt: img.imageAltText || '',
                    allowEmptySrc: true,
                  })}
                  draggable={false}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {/* ===== PREVIEW IMAGE ===== */}
        <Box className={styles.previewImage}>
          <img
            {...buildDsmImageProps({
              src: images[previewIndex].image,
              alt: images[previewIndex].imageAltText || '',
              allowEmptySrc: true,
            })}
          />
        </Box>

        {/* ===== BUTTONS ===== */}
        {activeIndex !== 0 && (
          <button className={styles.prevBtn} onClick={goPrev}>
            <ArrowLeftIcon />
          </button>
        )}

        {activeIndex !== images.length - 1 && (
          <button className={styles.nextBtn} onClick={goNext}>
            <ArrowRightIcon />
          </button>
        )}
      </Box>

      {/* ===== THUMBNAILS ===== */}
      <Thumbnails images={images} activeIndex={activeIndex} onSelect={handleThumbnailClick} />
    </>
  );
}
